import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import EmojiPicker from 'emoji-picker-react';
import { createIdea, createAIIdea } from '../api/ideas';
import { FaCog, FaLightbulb, FaMagic } from 'react-icons/fa';

function IdeaCreate({ token, refreshSidebar }) {
  const navigate = useNavigate();
  const [mode, setMode] = useState('assisted');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [emoji, setEmoji] = useState(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [createdIdea, setCreatedIdea] = useState(null);
  const [loadingText, setLoadingText] = useState('Reading your prompt...');
  const [progressPattern, setProgressPattern] = useState(0);

  // Loading texts for AI idea creation
  const loadingTexts = [
    'Reading your prompt...',
    'Firing up the idea neurons...',
    'Consulting the crystal ball...',
    'Brainstorming with my AI buddies...',
    'Crafting the perfect idea...',
    'Adding some secret sauce...',
    'Sprinkling creativity dust...',
    'Teaching components to dance...',
    'Organizing the chaos...',
    'Almost there... patience is key!',
    'Putting the final touches...'
  ];

  // Progress bar patterns
  const progressPatterns = ['progress-pattern-1', 'progress-pattern-2', 'progress-pattern-3'];

  // Sync text changes with progress bar resets
  useEffect(() => {
    if (!isLoading) return;
    
    let textIndex = 0;
    let patternIndex = 0;
    
    // Change text and progress pattern every 5 seconds (when progress bar resets)
    const interval = setInterval(() => {
      textIndex = (textIndex + 1) % loadingTexts.length;
      patternIndex = (patternIndex + 1) % progressPatterns.length;
      
      setLoadingText(loadingTexts[textIndex]);
      setProgressPattern(patternIndex);
    }, 5000); // Exactly 5 seconds to match progress bar cycle

    return () => clearInterval(interval);
  }, [isLoading]);

  const handleCreate = async () => {
    if (!token) {
      alert('Please log in to create an idea');
      return;
    }

    setIsLoading(true);
    setError(null);
    setLoadingText(loadingTexts[0]);

    try {
      let newIdeaId;
      let ideaResponse;
      
      if (mode === 'assisted') {
        // Use AI to generate the idea
        ideaResponse = await createAIIdea(description, token);
        
        // Try multiple possible ID field names
        newIdeaId = ideaResponse.id || 
                   ideaResponse.idea_id || 
                   ideaResponse.ideaId || 
                   ideaResponse.data?.id ||
                   ideaResponse.data?.idea_id;
        
        // Refresh sidebar for AI-generated idea too
        if (typeof refreshSidebar === 'function') {
          refreshSidebar();
        }
      } else {
        // Manual creation
        ideaResponse = await createIdea(
          {
            title,
            description,
            icon: emoji,
            status: 'draft',
            is_public: false,
            collaborator_ids: [],
          },
          token
        );
        newIdeaId = ideaResponse.id;
      }

      // Don't reset form or navigate automatically
      // Instead, show success state with navigation button
      
      // Refresh sidebar to show new idea
      if (typeof refreshSidebar === 'function') {
        refreshSidebar();
      }

      // Set the created idea for success display with more details
      if (newIdeaId) {
        const ideaTitle = mode === 'assisted' ? 
          (ideaResponse.title || ideaResponse.name || ideaResponse.data?.title || ideaResponse.data?.name || 'Untitled Idea') : 
          title;
        setCreatedIdea({ 
          id: newIdeaId, 
          title: ideaTitle,
          mode 
        });
      } else {
        // Show success anyway for AI ideas, since they might be created but ID extraction failed
        if (mode === 'assisted') {
          const ideaTitle = ideaResponse.title || ideaResponse.name || ideaResponse.data?.title || ideaResponse.data?.name || 'Untitled Idea';
          setCreatedIdea({
            id: 'unknown',
            title: ideaTitle,
            mode,
            noNavigation: true
          });
        } else {
          setError('Idea was created but navigation failed. Please check your ideas list.');
        }
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Animated loading screen component - memo to prevent animation resets
  const LoadingScreen = React.memo(({ pattern, text }) => (
    <div className="flex justify-center items-center min-h-full bg-[var(--bg-primary)] py-4 px-4 sm:py-8">
      <div className="w-full max-w-2xl bg-[var(--bg-secondary)] p-4 sm:p-8 rounded-xl shadow-lg">
        <div className="flex flex-col items-center justify-center space-y-6 sm:space-y-8">
          {/* Clean AI Processing Animation */}
          <div className="relative flex items-center justify-center w-full h-16 sm:h-20">
            
            {/* Central Processing Gear */}
            <div className="absolute">
              <FaCog 
                className="w-10 h-10 sm:w-12 sm:h-12 text-[var(--primary)] animate-spin"
                style={{ 
                  animationDuration: '5s'
                }}
              />
            </div>
            
            {/* Processing Indicators */}
            <div className="absolute -left-6 sm:-left-8 top-1/2 transform -translate-y-1/2">
              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[var(--text-hover)] rounded-full opacity-60"
                   style={{ animation: 'fadeInOut 2s ease-in-out infinite' }}>
              </div>
            </div>
            <div className="absolute left-6 sm:left-8 top-1/2 transform -translate-y-1/2">
              <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-[var(--text-hover)] rounded-full opacity-60"
                   style={{ animation: 'fadeInOut 2s ease-in-out infinite 1s' }}>
              </div>
            </div>
            
            {/* Processing Line */}
            <div className="absolute left-0 right-0 top-1/2 transform -translate-y-1/2 px-12 sm:px-16">
              <div 
                className="h-0.5 bg-gradient-to-r from-transparent via-[var(--primary)] to-transparent rounded-full"
                style={{
                  animation: 'line-pulse 5s ease-in-out infinite'
                }}
              />
            </div>
          </div>
          
          {/* Loading text section - separate from animations */}
          <div className="text-center">
            <h3 className="text-base sm:text-lg font-semibold text-[var(--text-primary)] mb-2 flex items-center justify-center gap-2">
              <FaMagic className="text-[var(--primary)] text-sm sm:text-base" />
              <span>Creating Your Idea</span>
            </h3>
            <div className="text-xs sm:text-sm text-[var(--text-secondary)] min-h-[20px] sm:min-h-[24px] flex items-center justify-center px-2">
              <span 
                className="text-center"
                style={{
                  animation: 'fadeInOut 0.5s ease-out'
                }}
              >
                {text}
              </span>
            </div>
          </div>
          
          {/* Progress indicator - dynamic pattern animation */}
          <div className="w-full max-w-xs">
            <div className="w-full bg-[var(--bg-hover)] rounded-full h-1.5 sm:h-2">
              <div 
                className="bg-[var(--primary)] h-1.5 sm:h-2 rounded-full transition-all duration-1000 ease-in-out"
                style={{ 
                  width: '100%',
                  animation: `${progressPatterns[pattern]} 5s ease-in-out infinite`
                }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  ));

  // Show success screen when idea is created
  if (createdIdea) {
    return (
      <div className="flex justify-center items-center min-h-full bg-[var(--bg-primary)] py-4 px-4 sm:py-8">
        <div className="w-full max-w-2xl bg-[var(--bg-secondary)] p-4 sm:p-8 rounded-xl shadow-lg text-center">
          <div className="flex flex-col items-center space-y-4 sm:space-y-6">
            {/* Animated Checkmark */}
            <div className="relative">
              <div 
                className="w-16 h-16 sm:w-20 sm:h-20 bg-[var(--secondary)] bg-opacity-20 rounded-full flex items-center justify-center"
                style={{
                  animation: 'zoomIn 0.8s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
                }}
              >
                <svg 
                  className="w-8 h-8 sm:w-10 sm:h-10 text-[var(--primary)]" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                  style={{
                    animation: 'zoomIn 1s cubic-bezier(0.68, -0.55, 0.265, 1.55) 0.2s both'
                  }}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
            </div>
            
            {/* Success Message */}
            <div className="space-y-2 sm:space-y-3">
              <h2 className="text-lg sm:text-2xl font-bold text-[var(--text-primary)] break-words">
                "{createdIdea.title}" created!
              </h2>
              <p className="text-sm sm:text-lg text-[var(--text-secondary)]">
                {createdIdea.mode === 'assisted' ? 
                  'Your AI-generated idea is ready to explore' : 
                  'Your idea is ready to work with'
                }
              </p>
            </div>
            
            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 pt-2 sm:pt-4 w-full sm:w-auto">
              {!createdIdea.noNavigation ? (
                <button
                  onClick={() => {
                    navigate(`/idea/${createdIdea.id}`);
                  }}
                  className="w-full sm:w-auto px-6 sm:px-8 py-3 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 active:scale-95 transition-all duration-200 font-medium shadow-lg text-sm sm:text-base"
                >
                  View Idea
                </button>
              ) : (
                <button
                  onClick={() => {
                    // Navigate to ideas list instead
                    navigate('/');
                  }}
                  className="w-full sm:w-auto px-6 sm:px-8 py-3 bg-[var(--primary)] text-white rounded-lg hover:opacity-90 active:scale-95 transition-all duration-200 font-medium shadow-lg text-sm sm:text-base"
                >
                  Check Ideas List
                </button>
              )}
              <button
                onClick={() => {
                  setCreatedIdea(null);
                  setTitle('');
                  setDescription('');
                  setEmoji(null);
                  setError(null);
                }}
                className="w-full sm:w-auto px-6 sm:px-8 py-3 bg-[var(--secondary)] text-white rounded-lg hover:bg-[var(--bg-hover)] active:scale-95 transition-all duration-200 font-medium border border-[var(--border-color)] text-sm sm:text-base"
              >
                Create Another
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Show loading screen when creating idea
  if (isLoading) {
    return <LoadingScreen pattern={progressPattern} text={loadingText} />;
  }

  return (
    <div className="flex justify-center items-center min-h-full bg-[var(--bg-primary)] py-4 px-4 sm:py-8">
      <div className="w-full max-w-2xl bg-[var(--bg-secondary)] p-4 sm:p-8 rounded-xl shadow-lg">
        <div className="flex justify-center mb-4 sm:mb-6 space-x-1">
          <button
            className={`flex-1 sm:flex-none px-4 sm:px-6 py-3 rounded-l-lg text-sm font-medium transition-colors duration-200 ${
              mode === 'assisted'
                ? 'bg-[var(--primary)] text-white'
                : 'bg-[var(--bg-hover)] text-[var(--text-primary)] hover:bg-[var(--bg-secondary)]'
            }`}
            onClick={() => setMode('assisted')}
          >
            <span className="flex items-center justify-center gap-1 sm:gap-2">
              <FaMagic className="text-xs sm:text-sm" />
              <span className="text-xs sm:text-sm">AI Assisted</span>
            </span>
          </button>
          <button
            className={`flex-1 sm:flex-none px-4 sm:px-6 py-3 rounded-r-lg text-sm font-medium transition-colors duration-200 ${
              mode === 'manual'
                ? 'bg-[var(--primary)] text-white'
                : 'bg-[var(--bg-hover)] text-[var(--text-primary)] hover:bg-[var(--bg-secondary)]'
            }`}
            onClick={() => setMode('manual')}
          >
            <span className="flex items-center justify-center gap-1 sm:gap-2">
              <FaLightbulb className="text-xs sm:text-sm" />
              <span className="text-xs sm:text-sm">Manual</span>
            </span>
          </button>
        </div>

        {mode === 'assisted' ? (
          <div className="space-y-3 sm:space-y-4">
            <textarea
              className="w-full h-48 sm:h-64 p-3 sm:p-4 border border-[var(--border-color)] rounded-lg resize-none focus:ring-2 focus:ring-[var(--primary)] focus:border-transparent text-[var(--text-primary)] placeholder-[var(--text-secondary)] bg-[var(--bg-primary)] text-sm sm:text-base"
              placeholder="Describe your idea in detail...

Examples:
• A workout tracker with progress charts
• A recipe organizer with ingredient tables  
• A study planner with tasks and notes
• A travel journal with photo galleries

Be specific about what components you need!"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={{ fontSize: '16px' }} // Prevent zoom on iOS
            />
            <div className="text-xs sm:text-sm text-[var(--text-secondary)] flex items-start gap-2 p-3 bg-[var(--bg-primary)] rounded-lg border border-[var(--border-color)]">
              <FaLightbulb className="text-yellow-500 mt-0.5 flex-shrink-0" />
              <span>Tip: Mention what types of components you need (tables, notes, galleries, timers, etc.)</span>
            </div>
          </div>
        ) : (
          <div className="space-y-3 sm:space-y-4">
            <div className="flex items-center gap-3">
              <div className="relative">
                <button
                  className="p-3 border border-[var(--border-color)] rounded-lg hover:bg-[var(--bg-hover)] active:scale-95 transition-all duration-200 text-2xl bg-[var(--bg-secondary)] min-w-[56px] h-[56px] flex items-center justify-center"
                  onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  aria-label="Pick emoji"
                >
                  {emoji || '😊'}
                </button>
                {showEmojiPicker && (
                  <>
                    <div 
                      className="fixed inset-0 z-10 bg-black/20" 
                      onClick={() => setShowEmojiPicker(false)}
                    />
                    <div className="absolute z-20 top-full left-0 mt-2">
                      <EmojiPicker
                        onEmojiClick={(emojiData) => {
                          setEmoji(emojiData.emoji);
                          setShowEmojiPicker(false);
                        }}
                        width={280}
                        height={350}
                      />
                    </div>
                  </>
                )}
              </div>
              <input
                type="text"
                className="flex-1 p-3 border border-[var(--border-color)] rounded-lg focus:ring-2 focus:ring-[var(--primary)] focus:border-transparent text-[var(--text-primary)] placeholder-[var(--text-secondary)] bg-[var(--bg-primary)] text-sm sm:text-base"
                placeholder="Enter your idea title..."
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                style={{ fontSize: '16px' }} // Prevent zoom on iOS
              />
            </div>
            <textarea
              className="w-full h-24 sm:h-32 p-3 sm:p-4 border border-[var(--border-color)] rounded-lg resize-none focus:ring-2 focus:ring-[var(--primary)] focus:border-transparent text-[var(--text-primary)] placeholder-[var(--text-secondary)] bg-[var(--bg-primary)] text-sm sm:text-base"
              placeholder="Describe your idea (optional)..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              style={{ fontSize: '16px' }} // Prevent zoom on iOS
            />
          </div>
        )}

        {error && (
          <div className="mt-3 sm:mt-4 p-3 bg-danger-light text-danger rounded-lg text-sm">
            {error}
          </div>
        )}

        <button
          className={`mt-4 sm:mt-6 w-full py-3 sm:py-4 rounded-lg text-sm sm:text-base font-medium transition-all duration-200 flex items-center justify-center gap-2 ${
            (mode === 'assisted' ? description : title || description)
              ? 'bg-[var(--primary)] text-white hover:opacity-90 active:scale-95 transform shadow-lg'
              : 'bg-[var(--bg-hover)] text-[var(--text-secondary)] cursor-not-allowed'
          }`}
          disabled={isLoading || (mode === 'assisted' ? !description : !title && !description)}
          onClick={handleCreate}
        >
          {mode === 'assisted' ? (
            <>
              <FaMagic className="text-sm sm:text-base" />
              <span>Create with AI Magic</span>
            </>
          ) : (
            <>
              <FaLightbulb className="text-sm sm:text-base" />
              <span>Create Idea</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
}

export default IdeaCreate;